import { useCallback, useMemo, useState } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import type { ConfiguredProductUnit } from '../types/productUnit'
import { getProductUnitCode } from '../utils/getProductUnitCode'

const MAX_RECENT_PRODUCT_UNITS = 5

const readRecentUnitKeys = (storageKey: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '[]')

    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []
  } catch {
    return []
  }
}

const writeRecentUnitKeys = (storageKey: string, unitKeys: string[]) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(unitKeys))
  } catch {
    // Storage unavailable or full: recent units just aren't remembered
  }
}

// Standard units are remembered by their UN/ECE code, which does not change with the UI language.
// Custom units have no code, so their name is the stable key.
const getRecentUnitKey = (unit: ConfiguredProductUnit): string => getProductUnitCode(unit) ?? unit.name

const findRecentUnit = (units: ConfiguredProductUnit[], key: string): ConfiguredProductUnit | undefined =>
  units.find((unit) => getProductUnitCode(unit) === key) ?? units.find((unit) => unit.name === key)

const getStoredUnitKey = (unitName: string, units: ConfiguredProductUnit[]): string => {
  const unit = units.find((configuredUnit) => configuredUnit.name === unitName)

  return unit ? getRecentUnitKey(unit) : unitName
}

// A name saved before codes were introduced is rewritten once it matches the current language,
// so the same unit is not remembered twice.
const toRecentUnitKeys = (keys: string[], units: ConfiguredProductUnit[]): string[] => {
  const stableKeys = keys.map((key) => {
    const unit = findRecentUnit(units, key)
    return unit ? getRecentUnitKey(unit) : key
  })
  return [...new Set(stableKeys)]
}

// Read from storage on demand rather than with useLocalStorage: every invoice line renders its own
// select, and useLocalStorage state is not shared between instances
export const useRecentProductUnits = (units: ConfiguredProductUnit[]) => {
  const [recentUnitKeys, setRecentUnitKeys] = useState<string[]>([])
  const { organization } = useUserOrganization()

  const storageKey = organization?.id ? `${LocalStorageKeys.RecentProductUnits}-${organization.id}` : undefined

  const recentUnits = useMemo(
    () =>
      recentUnitKeys.flatMap((key) => {
        const unit = findRecentUnit(units, key)

        return unit ? [unit] : []
      }),
    [recentUnitKeys, units],
  )

  const saveRecentUnitKeys = useCallback(
    (getKeys: (unitKeys: string[]) => string[]) => {
      if (!storageKey) {
        return
      }

      const unitKeys = toRecentUnitKeys(getKeys(readRecentUnitKeys(storageKey)), units).slice(
        0,
        MAX_RECENT_PRODUCT_UNITS,
      )
      writeRecentUnitKeys(storageKey, unitKeys)
      setRecentUnitKeys(unitKeys)
    },
    [storageKey, units],
  )

  const refreshRecentUnits = useCallback(() => {
    if (!storageKey) {
      setRecentUnitKeys([])
      return
    }

    saveRecentUnitKeys((unitKeys) => unitKeys)
  }, [saveRecentUnitKeys, storageKey])

  const addRecentUnit = useCallback(
    (unitName: string) => {
      const key = getStoredUnitKey(unitName, units)

      saveRecentUnitKeys((unitKeys) => [key, ...unitKeys])
    },
    [saveRecentUnitKeys, units],
  )

  const removeRecentUnit = useCallback(
    (unitName: string) => {
      const key = getStoredUnitKey(unitName, units)

      saveRecentUnitKeys((unitKeys) => toRecentUnitKeys(unitKeys, units).filter((storedKey) => storedKey !== key))
    },
    [saveRecentUnitKeys, units],
  )

  return { addRecentUnit, recentUnits, refreshRecentUnits, removeRecentUnit }
}
